import { useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
// eslint-disable-next-line no-unused-vars -- temporarily hidden panels; imports
// retained so re-enabling in Settings is a one-line change (see comment below
// the DeleteAccountPanel render call for details).
import { DataPrivacyPanel } from '../components/DataPrivacyPanel';
import { DeleteAccountPanel } from '../components/DeleteAccountPanel';
// eslint-disable-next-line no-unused-vars
import { AccessLogPanel } from '../components/AccessLogPanel';
import { Save, Mail, Send, Bell, Lock, Download, Upload, ShieldCheck, Loader2, Building2, Pencil, Info, ScanText } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { PageHeader } from '../components/PageHeader';
import { PasswordInput } from '../components/PasswordInput';
import { WhatsappSettingsPanel } from '../components/WhatsappSettingsPanel';

// Feature-flag WhatsApp UI. Set REACT_APP_ENABLE_WHATSAPP="true" to expose it.
// Kept OFF until Meta WhatsApp Cloud API is approved and credentials are plugged in.
const WHATSAPP_ENABLED = (process.env.REACT_APP_ENABLE_WHATSAPP || 'false').toLowerCase() === 'true';
import { BillingSection } from '../components/BillingSection';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

export default function SettingsPage() {
    const { user } = useAuth();
    const { workspace, openBusinessSetup, refreshSettings } = useOutletContext() || {};
    const [s, setS] = useState(null);
    const [saving, setSaving] = useState(false);
    const [pwd, setPwd] = useState('');
    // Sprint 2026-07: probe whether this user is on the scanner allowlist.
    // The "Scanner Tools" toggle card is only rendered if the probe returns
    // 200; regular tenants never see it.
    const [isScannerUser, setIsScannerUser] = useState(false);

    // password change
    const [currentPw, setCurrentPw] = useState('');
    const [newPw, setNewPw] = useState('');
    const [newPw2, setNewPw2] = useState('');
    const [changing, setChanging] = useState(false);

    // Backup / restore
    const [exporting, setExporting] = useState(false);
    const [pendingImport, setPendingImport] = useState(null); // { fileName, payload, counts }
    const [importing, setImporting] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        api.getSettings().then(setS);
        api.scannerWhoami()
            .then(() => setIsScannerUser(true))
            .catch(() => setIsScannerUser(false));
    }, []);

    if (!s) return <div>Loading…</div>;

    async function save() {
        setSaving(true);
        try {
            const body = { ...s };
            if (pwd) body.smtp_password = pwd;
            const updated = await api.updateSettings(body);
            setS(updated);
            setPwd('');
            toast.success('Settings saved');
            // Sprint C: refresh App-level settings so the Sidebar/Topbar
            // show/hide the Alerts entry immediately.
            try { refreshSettings?.(); } catch { /* noop */ }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to save');
        } finally {
            setSaving(false);
        }
    }

    /**
     * Sprint C: auto-persist a single toggle change *immediately*. Users don't
     * expect to have to hit "Save settings" for a switch flip; not doing so
     * was causing the alerts toggle to "revert to on" after navigating away
     * (because the click never made it to the DB).
     *
     * Only sends the delta so we don't clobber unsaved threshold edits.
     */
    async function saveToggle(patch) {
        // Optimistic UI: flip local state first so the switch feels instant.
        setS((prev) => ({ ...prev, ...patch }));
        try {
            const updated = await api.updateSettings(patch);
            setS(updated);
            const label = Object.keys(patch)[0];
            const on = Object.values(patch)[0];
            const pretty = label === 'alerts_enabled' ? 'Price change alerts'
                : label === 'weekly_report_enabled' ? 'Weekly reports'
                : label === 'scanner_enabled' ? 'Scanner tools'
                : label;
            toast.success(`${pretty} ${on ? 'enabled' : 'disabled'}`);
            try { refreshSettings?.(); } catch { /* noop */ }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to save toggle');
            // Roll back optimistic change on failure
            const original = Object.fromEntries(
                Object.entries(patch).map(([k, v]) => [k, !v]),
            );
            setS((prev) => ({ ...prev, ...original }));
        }
    }

    async function sendTest() {
        try {
            await api.testEmail();
            toast.success('Test email sent');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Test email failed');
        }
    }

    async function changePw(e) {
        e.preventDefault();
        if (newPw.length < 8) return toast.error('New password must be at least 8 characters');
        if (newPw !== newPw2) return toast.error("New passwords don't match");
        setChanging(true);
        try {
            await api.changePassword(currentPw, newPw);
            setCurrentPw('');
            setNewPw('');
            setNewPw2('');
            toast.success('Password changed');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to change password');
        } finally {
            setChanging(false);
        }
    }

    async function handleExport() {
        setExporting(true);
        try {
            const { blob, filename } = await api.exportData();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success('Backup downloaded');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to export');
        } finally {
            setExporting(false);
        }
    }

    function pickImportFile(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            try {
                const parsed = JSON.parse(String(reader.result || ''));
                const counts = parsed?.counts || {};
                setPendingImport({ fileName: file.name, payload: parsed, counts });
            } catch (_err) {
                toast.error('That file is not valid JSON');
            } finally {
                if (fileInputRef.current) fileInputRef.current.value = '';
            }
        };
        reader.onerror = () => {
            toast.error('Could not read the file');
        };
        reader.readAsText(file);
    }

    async function confirmImport() {
        if (!pendingImport?.payload) return;
        setImporting(true);
        try {
            const res = await api.importData(pendingImport.payload, 'replace');
            const summary = res?.summary?.collections || {};
            const totalInserted = Object.values(summary).reduce(
                (n, c) => n + (c?.inserted || 0),
                0,
            );
            toast.success(`Restored ${totalInserted} records from ${pendingImport.fileName}`);
            setPendingImport(null);
            // trigger a soft page refresh so dashboards recalculate against restored data
            setTimeout(() => window.location.reload(), 400);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to import');
        } finally {
            setImporting(false);
        }
    }

    return (
        <div className="space-y-5 animate-fade-in max-w-3xl">
            <PageHeader
                title="Settings"
                subtitle="Configure email notifications, price change alert thresholds, and your account password."
            />

            {/* --- Billing (Phase 18) --- */}
            <BillingSection />

            {/* --- WhatsApp bill upload (gated: enable after Meta Cloud API approves) --- */}
            {WHATSAPP_ENABLED && <WhatsappSettingsPanel />}

            {/* --- Business profile (PAN + GSTINs, Phase 16) --- */}
            <Card className="p-5 border-border/80" data-testid="settings-business-card">
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-lg bg-accent text-accent-foreground flex items-center justify-center">
                            <Building2 size={18} />
                        </div>
                        <div>
                            <div className="font-heading text-lg">Business profile</div>
                            <div className="text-xs text-muted-foreground">
                                Your PAN and GSTINs. Bills are matched against these so
                                mis-routed uploads get flagged.
                            </div>
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openBusinessSetup?.()}
                        data-testid="edit-business-btn"
                    >
                        <Pencil size={14} className="mr-1" />
                        {workspace ? 'Edit' : 'Set up'}
                    </Button>
                </div>
                <Separator className="mb-4" />
                {workspace ? (
                    <div className="space-y-3 text-sm">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <div className="text-xs uppercase tracking-wide text-muted-foreground">Business name</div>
                                <div className="font-medium">{workspace.business_name}</div>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wide text-muted-foreground">Business type</div>
                                <div className="capitalize">{workspace.business_type || <span className="text-muted-foreground">—</span>}</div>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wide text-muted-foreground">PAN</div>
                                <div className="font-mono tracking-wider">{workspace.pan}</div>
                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                    {workspace.entity_type_label}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wide text-muted-foreground">State</div>
                                <div>{workspace.state || <span className="text-muted-foreground">—</span>}</div>
                            </div>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                                Registered GSTINs ({workspace.gstins?.length || 0})
                            </div>
                            {workspace.gstins?.length ? (
                                <div className="flex flex-wrap gap-2">
                                    {workspace.gstins.map((g) => (
                                        <Badge key={g.gstin} variant="outline" className="font-mono text-[11px]">
                                            {g.gstin}
                                            {g.state && (
                                                <span className="ml-1.5 text-muted-foreground font-sans">
                                                    · {g.state}
                                                </span>
                                            )}
                                        </Badge>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-xs text-muted-foreground italic">
                                    No GSTINs added. If your turnover is under ₹40L you don't need one.
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="text-sm text-muted-foreground">
                        You haven't set up your business profile yet.
                        <Button
                            variant="link"
                            className="px-1 h-auto"
                            onClick={() => openBusinessSetup?.()}
                        >
                            Set it up now →
                        </Button>
                    </div>
                )}
            </Card>

            {/* --- Price alert thresholds --- */}
            <Card className="p-5 border-border/80">
                <div className="flex items-start gap-3 mb-4">
                    <div className="h-9 w-9 rounded-lg bg-accent text-accent-foreground flex items-center justify-center shrink-0">
                        <Bell size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-heading text-lg">Price change alerts</div>
                        <div className="text-xs text-muted-foreground">
                            Notifies you when an item's GST-inclusive price changes beyond your thresholds.
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <span className="text-sm text-muted-foreground">
                            {s.alerts_enabled === false ? 'Off' : 'On'}
                        </span>
                        <Switch
                            data-testid="settings-alerts-enabled"
                            checked={s.alerts_enabled !== false}
                            onCheckedChange={(v) => saveToggle({ alerts_enabled: v })}
                        />
                    </div>
                </div>
                <Separator className="mb-4" />
                <div
                    className={`grid grid-cols-1 md:grid-cols-2 gap-4 transition-opacity ${s.alerts_enabled === false ? 'opacity-50 pointer-events-none' : ''}`}
                    data-testid="settings-alert-thresholds-body"
                >
                    <div>
                        <Label>Alert on increases when ≥</Label>
                        <div className="flex items-center gap-2">
                            <Input
                                data-testid="settings-inc-threshold"
                                type="number"
                                min={0}
                                step={0.1}
                                value={s.alert_increase_percent ?? 0}
                                onChange={(e) =>
                                    setS({ ...s, alert_increase_percent: Number(e.target.value) || 0 })
                                }
                            />
                            <span className="text-sm text-muted-foreground">%</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                            e.g. 5 = only when a price goes up by at least 5%
                        </div>
                    </div>
                    <div>
                        <Label>Alert on decreases when ≥</Label>
                        <div className="flex items-center gap-2">
                            <Input
                                data-testid="settings-dec-threshold"
                                type="number"
                                min={0}
                                step={0.1}
                                value={s.alert_decrease_percent ?? 0}
                                onChange={(e) =>
                                    setS({ ...s, alert_decrease_percent: Number(e.target.value) || 0 })
                                }
                            />
                            <span className="text-sm text-muted-foreground">%</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                            e.g. 10 = only when a price drops by at least 10%
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 mt-5">
                    <Button onClick={save} disabled={saving || s.alerts_enabled === false} className="gap-2" data-testid="settings-save-button">
                        <Save size={16} /> {saving ? 'Saving…' : 'Save thresholds'}
                    </Button>
                    {s.alerts_enabled === false && (
                        <span className="text-xs text-muted-foreground">Turn alerts on to edit thresholds.</span>
                    )}
                </div>
            </Card>

            {/* --- Weekly reports (Sprint C) --- */}
            <Card className="p-5 border-border/80">
                <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-lg bg-accent text-accent-foreground flex items-center justify-center shrink-0">
                        <Mail size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-heading text-lg">Weekly reports</div>
                        <div className="text-sm text-muted-foreground mt-1">
                            A weekly summary of your bills, spend, payments, and price alerts is emailed
                            every <strong>Monday at 9:00 AM IST</strong> to{' '}
                            <span className="font-mono">{user?.email}</span>.
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <span className="text-sm text-muted-foreground">
                            {s.weekly_report_enabled === false ? 'Off' : 'On'}
                        </span>
                        <Switch
                            data-testid="settings-weekly-report-enabled"
                            checked={s.weekly_report_enabled !== false}
                            onCheckedChange={(v) => saveToggle({ weekly_report_enabled: v })}
                        />
                    </div>
                </div>
            </Card>

            {/* --- Scanner Tools (Sprint 2026-07) --- */}
            {/* Only rendered for users on the OWNER_SCANNER_EMAILS allowlist.
                Backend also silently ignores scanner_enabled updates from
                anyone outside the allowlist (see /api/settings POST). */}
            {isScannerUser && (
                <Card className="p-5 border-border/80" data-testid="settings-scanner-tools-card">
                    <div className="flex items-start gap-3">
                        <div className="h-9 w-9 rounded-lg bg-accent text-accent-foreground flex items-center justify-center shrink-0">
                            <ScanText size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-heading text-lg">Scanner tools</div>
                            <div className="text-sm text-muted-foreground mt-1">
                                Show the <strong>Daily Sheet</strong> and <strong>Vijay Ras</strong> pages in the
                                sidebar. These handwritten-sheet OCR tools are invite-only and
                                automatically route extracted data into Expenses, Staff, and
                                the Vijay Ras ledger. Turn off any time — your saved sheets
                                stay intact.
                            </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <span className="text-sm text-muted-foreground">
                                {s.scanner_enabled === true ? 'On' : 'Off'}
                            </span>
                            <Switch
                                data-testid="settings-scanner-enabled"
                                checked={s.scanner_enabled === true}
                                onCheckedChange={(v) => saveToggle({ scanner_enabled: v })}
                            />
                        </div>
                    </div>
                </Card>
            )}

            {/* --- Account / password --- */}
            <Card className="p-5 border-border/80">
                <div className="flex items-center gap-3 mb-4">
                    <div className="h-9 w-9 rounded-lg bg-accent text-accent-foreground flex items-center justify-center">
                        <Lock size={18} />
                    </div>
                    <div>
                        <div className="font-heading text-lg">Account</div>
                        <div className="text-xs text-muted-foreground">Signed in as {user?.email}</div>
                    </div>
                </div>
                <Separator className="mb-4" />
                <form onSubmit={changePw} className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="settings-change-password-form">
                    <div>
                        <Label>Current password</Label>
                        <PasswordInput
                            data-testid="settings-current-password"
                            required
                            value={currentPw}
                            onChange={(e) => setCurrentPw(e.target.value)}
                        />
                    </div>
                    <div>
                        <Label>New password</Label>
                        <PasswordInput
                            data-testid="settings-new-password"
                            minLength={8}
                            required
                            value={newPw}
                            onChange={(e) => setNewPw(e.target.value)}
                        />
                    </div>
                    <div>
                        <Label>Confirm new password</Label>
                        <PasswordInput
                            data-testid="settings-confirm-password"
                            minLength={8}
                            required
                            value={newPw2}
                            onChange={(e) => setNewPw2(e.target.value)}
                        />
                    </div>
                    <div className="md:col-span-3">
                        <Button type="submit" disabled={changing} data-testid="settings-change-password-submit">
                            {changing ? 'Updating…' : 'Change password'}
                        </Button>
                    </div>
                </form>
            </Card>

            <Card className="p-5 border-border/80" data-testid="settings-backup-card">
                <div className="flex items-center gap-3 mb-4">
                    <div className="h-9 w-9 rounded-lg bg-accent text-accent-foreground flex items-center justify-center">
                        <ShieldCheck size={18} />
                    </div>
                    <div>
                        <div className="font-heading text-lg">Backup &amp; restore</div>
                        <div className="text-xs text-muted-foreground">
                            Export your bills, payments, alerts and settings as a single JSON file.
                            You can re-import it later to restore your workspace.
                        </div>
                    </div>
                </div>
                <Separator className="mb-4" />
                <div className="rounded-md border border-border bg-muted/30 px-3 py-2 mb-4 text-xs text-muted-foreground">
                    Your login credentials are already auto-persisted, so you should never get locked
                    out again even if the workspace resets. Use this backup for your business data
                    (bills, payments, alerts).
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        onClick={handleExport}
                        disabled={exporting}
                        className="gap-2"
                        data-testid="settings-export-btn"
                    >
                        {exporting ? (
                            <>
                                <Loader2 size={16} className="animate-spin" /> Exporting…
                            </>
                        ) : (
                            <>
                                <Download size={16} /> Download backup
                            </>
                        )}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        className="gap-2"
                        data-testid="settings-import-btn"
                    >
                        <Upload size={16} /> Restore from backup…
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/json,.json"
                        className="hidden"
                        onChange={pickImportFile}
                        data-testid="settings-import-file-input"
                    />
                </div>
            </Card>

            <DeleteAccountPanel />

            {/* NOTE (2026-07-20): Temporarily hidden per owner request.
                Kept as commented-out JSX rather than removed so we can
                re-enable in one line when the trust page is finalized.

                • DataPrivacyPanel  — the "Data & privacy / Who can see my
                  data" disclosure card.
                • AccessLogPanel    — the per-workspace access log
                  (GET /api/account/access-log).

                Both features remain fully implemented on backend + client;
                we're only suppressing them in the Settings UI for now.

                <DataPrivacyPanel />
                <AccessLogPanel />
            */}

            {/* About should be the LAST card so users end their Settings
                scroll on a friendly product blurb rather than a destructive
                action. */}
            <Card className="p-5 border-border/80">
                <div className="font-heading text-base mb-1">About Ease My Bill</div>
                <p className="text-sm text-muted-foreground">
                    Ease My Bill uses AI Vision to extract line items from your bills and tracks the unit price of every item
                    per vendor. When the same item&rsquo;s price differs from the previous bill by more than your threshold, we
                    surface an alert on the dashboard and on the Alerts page. Weekly summaries and receipts are emailed
                    to your registered address automatically.
                </p>
            </Card>

            <AlertDialog
                open={!!pendingImport}
                onOpenChange={(v) => !v && setPendingImport(null)}
            >
                <AlertDialogContent data-testid="settings-import-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Restore workspace from this backup?</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-2">
                                <div>
                                    File:{' '}
                                    <span className="font-mono text-foreground">
                                        {pendingImport?.fileName}
                                    </span>
                                </div>
                                {pendingImport?.counts && Object.keys(pendingImport.counts).length > 0 && (
                                    <div>
                                        Records in this backup:
                                        <ul className="mt-1 space-y-0.5 text-sm">
                                            {Object.entries(pendingImport.counts).map(([k, v]) => (
                                                <li key={k} className="font-mono">
                                                    · {k}: {v}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                <div className="text-[hsl(var(--price-increase-fg))]">
                                    This will <b>replace</b> your current bills, payments, alerts and
                                    settings with the contents of this file. Your login credentials
                                    stay untouched.
                                </div>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={importing} data-testid="settings-import-cancel">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                confirmImport();
                            }}
                            disabled={importing}
                            data-testid="settings-import-confirm-btn"
                        >
                            {importing ? 'Restoring…' : 'Restore workspace'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
